import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** `slot` is null for the one commit before the footer bar has mounted. */
interface FooterSlot {
  slot: HTMLDivElement | null;
}

const FooterSlotContext = createContext<FooterSlot | null>(null);

/** Wraps the wizard's scrollable body + a pinned footer bar. Every step's
 *  `<WizardFooter>` portals into that bar instead of scrolling away with the
 *  step's own content — see `WizardFooter` below. */
export function ImportWizardFooterProvider({ children }: { children: ReactNode }) {
  // A ref callback that sets state runs during commit, and React applies
  // that update before the browser paints, so no step footer ever flashes
  // outside the bar — and no DOM node has to be created by hand.
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const footer = useMemo(() => ({ slot }), [slot]);

  return (
    <FooterSlotContext.Provider value={footer}>
      {children}
      <div ref={setSlot} className="flex shrink-0 items-center border-t border-border px-6 py-4" />
    </FooterSlotContext.Provider>
  );
}

/** A step's bottom action row (Continue/Upload/Import button, row counts,
 *  download-template link, etc.). Portals into the wizard's pinned footer bar
 *  when rendered inside `ImportWizardFooterProvider`; falls back to rendering
 *  inline when a step is unit-tested standalone (no provider ancestor). */
export function WizardFooter({ children }: { children: ReactNode }) {
  const footer = useContext(FooterSlotContext);
  if (footer === null) return <>{children}</>;
  if (footer.slot === null) return null;
  return createPortal(children, footer.slot);
}
